import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Clock,
  Award,
  CheckCircle2,
  Users,
  BookOpen,
  Calendar,
  Download,
  Share2,
  Target,
  TrendingUp,
  Shield
} from 'lucide-react';
import { standards } from '../data/standards/standardsData';

/**
 * TrainingCategoryDetail Component
 *
 * SEO-optimized individual page for each training category within a standard
 * Each page has unique content, meta tags, and structured data for better ranking
 *
 * URL Structure: /training/{standard-slug}/{category}
 * Example: /training/iso-9001/awareness
 *          /training/iso-9001/internal-auditor
 *          /training/iso-9001/lead-auditor
 */
const TrainingCategoryDetail = () => {
  const { standardSlug, category } = useParams();
  const navigate = useNavigate();

  const standard = standards[standardSlug];

  useEffect(() => {
    window.scrollTo(0, 0);

    // Redirect to dedicated SEO-optimized pages for Lead Auditor courses
    if (category === 'lead-auditor') {
      const leadAuditorRedirects = {
        'iso-9001': '/Lead-Auditor-Certification-ISO-9001',
        'iso-14001': '/ISO-14001-Lead-Auditor-Course',
        'iso-27001': '/ISO-27001-Lead-Auditor-Course',
        'iso-45001': '/ISO-45001-Lead-Auditor-Course',
        'iso-13485': '/ISO-13485-Lead-Auditor-Training'
      };

      if (leadAuditorRedirects[standardSlug]) {
        navigate(leadAuditorRedirects[standardSlug], { replace: true });
        return;
      }
    }
  }, [standardSlug, category, navigate]);

  // Validate standard and category
  if (!standard) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Standard Not Found</h1>
          <button
            onClick={() => navigate('/training/iso-training-services')}
            className="text-blue-600 hover:underline"
          >
            Return to ISO Training Services
          </button>
        </div>
      </div>
    );
  }

  // Validate category parameter
  if (!category) {
    navigate(`/training/${standardSlug}`);
    return null;
  }

  // Convert URL category to data key
  const categoryKey = category === 'internal-auditor' ? 'internalAuditor'
                    : category === 'lead-auditor' ? 'leadAuditor'
                    : 'awareness';

  const topics = standard.keyTopics[categoryKey];

  if (!topics) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Training Category Not Found</h1>
          <button
            onClick={() => navigate(`/training/${standardSlug}`)}
            className="text-blue-600 hover:underline"
          >
            Return to {standard.code}
          </button>
        </div>
      </div>
    );
  }

  // Training category metadata
  const categoryMeta = {
    awareness: {
      title: 'Awareness Training',
      duration: '1 Day',
      level: 'Beginner',
      description: 'Comprehensive awareness training designed to provide a foundational understanding of the standard, its requirements, and implementation principles.',
      targetAudience: [
        'New quality/management professionals',
        'Team members involved in system implementation',
        'Department heads and supervisors',
        'Anyone seeking foundational knowledge'
      ],
      outcomes: [
        'Understand the structure and requirements of the standard',
        'Learn key principles and concepts',
        'Recognize benefits of implementation',
        'Prepare for system implementation or certification'
      ],
      certification: 'Certificate of Attendance',
      prerequisites: 'No prior knowledge required'
    },
    internalAuditor: {
      title: 'Internal Auditor Training',
      duration: '3 Days',
      level: 'Intermediate',
      description: 'Intensive training program that equips participants with practical skills to conduct effective internal audits and ensure compliance with standard requirements.',
      targetAudience: [
        'Internal auditors and audit team members',
        'Quality managers and coordinators',
        'Compliance officers',
        'Process owners responsible for audits'
      ],
      outcomes: [
        'Plan and conduct systematic internal audits',
        'Identify non-conformities and opportunities for improvement',
        'Write effective audit reports',
        'Apply audit best practices and techniques'
      ],
      certification: 'Certified Internal Auditor',
      prerequisites: 'Basic understanding of the standard recommended'
    },
    leadAuditor: {
      title: 'Lead Auditor Training',
      duration: '5 Days',
      level: 'Advanced',
      description: 'Comprehensive certification program preparing professionals to lead audit teams, manage audit programs, and conduct third-party certification audits.',
      targetAudience: [
        'Experienced internal auditors',
        'Quality management professionals',
        'Consultants and certification body auditors',
        'Senior management system professionals'
      ],
      outcomes: [
        'Lead and manage audit teams effectively',
        'Conduct certification and third-party audits',
        'Manage complex audit programs',
        'Meet international auditor competence requirements'
      ],
      certification: 'Certified Lead Auditor (CQI/IRCA Recognized)',
      prerequisites: 'Internal auditor certification or equivalent experience'
    }
  };

  const meta = categoryMeta[categoryKey];
  const categoryDisplayName = category
    ? category.split('-').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    : 'Training';

  // SEO metadata
  const pageTitle = `${standard.code} ${meta.title} - ${meta.duration} Certification Course | ISDE Academy`;
  const pageDescription = `${meta.description} ${meta.duration} ${standard.code} ${categoryDisplayName} training with expert instructors. Enroll now for ${meta.certification}.`;
  const keywords = `${standard.code} ${categoryDisplayName}, ${standard.name} ${categoryDisplayName}, ${standard.code} training, ${categoryDisplayName} course, ${categoryDisplayName} certification, ${standard.seo.keywords}`;

  // Structured data for SEO
  const courseStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${standard.code} ${meta.title}`,
    "description": pageDescription,
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://www.isdeacademy.com"
    },
    "courseCode": `${standard.slug}-${category}`,
    "educationalLevel": meta.level,
    "timeRequired": meta.duration,
    "coursePrerequisites": meta.prerequisites,
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "courseWorkload": meta.duration
    },
    "occupationalCredentialAwarded": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": meta.certification
    }
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.isdeacademy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Training Programs",
        "item": "https://www.isdeacademy.com/training/iso-training-services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": standard.code,
        "item": `https://www.isdeacademy.com/training/${standardSlug}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": meta.title,
        "item": `https://www.isdeacademy.com/training/${standardSlug}/${category}`
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={keywords} />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.isdeacademy.com/training/${standardSlug}/${category}`} />
        {standard.seo.ogImage && <meta property="og:image" content={standard.seo.ogImage} />}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />

        {/* Canonical URL */}
        <link rel="canonical" href={`https://www.isdeacademy.com/training/${standardSlug}/${category}`} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(courseStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        {/* Hero Section */}
        <section className={`relative bg-gradient-to-br ${standard.color.gradient} text-white pt-24 pb-16 mt-16 overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumb Navigation */}
            <nav className="flex items-center gap-2 text-sm text-white/80 mb-6">
              <Link to="/training/iso-training-services" className="hover:text-white transition-colors">
                ISO Training Services
              </Link>
              <ArrowRight className="h-4 w-4" />
              <Link to={`/training/${standardSlug}`} className="hover:text-white transition-colors">
                {standard.code}
              </Link>
              <ArrowRight className="h-4 w-4" />
              <span className="text-white font-medium">{meta.title}</span>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Course Badge */}
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Award className="h-5 w-5" />
                <span className="text-sm font-semibold">{meta.level} Level • {meta.duration}</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {standard.code}<br />
                <span className="text-white/90">{meta.title}</span>
              </h1>

              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl leading-relaxed">
                {meta.description}
              </p>

              {/* Quick Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="h-5 w-5" />
                    <span className="font-semibold">Duration</span>
                  </div>
                  <p className="text-white/90">{meta.duration}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="h-5 w-5" />
                    <span className="font-semibold">Level</span>
                  </div>
                  <p className="text-white/90">{meta.level}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="flex items-center gap-3 mb-2">
                    <Award className="h-5 w-5" />
                    <span className="font-semibold">Certification</span>
                  </div>
                  <p className="text-white/90 text-sm">{meta.certification}</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/training-calendar"
                  className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <Calendar className="h-5 w-5" />
                  View Training Schedule
                </Link>
                <Link
                  to="/contact"
                  className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/30 flex items-center gap-2"
                >
                  <Users className="h-5 w-5" />
                  Contact Us
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - Left Side */}
            <div className="lg:col-span-2 space-y-12">
              {/* Course Overview */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                  <BookOpen className={`h-8 w-8 ${standard.color.text}`} />
                  Course Overview
                </h2>
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    {meta.description}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    This {meta.duration} comprehensive training program is specifically designed for {meta.targetAudience[0].toLowerCase()}
                    and provides in-depth knowledge of {standard.code} - {standard.name}. Our expert-led course combines
                    theoretical understanding with practical application to ensure participants gain real-world skills.
                  </p>
                </div>
              </motion.section>

              {/* Learning Outcomes */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                  <Target className={`h-8 w-8 ${standard.color.text}`} />
                  Learning Outcomes
                </h2>
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                  <p className="text-gray-600 mb-6">
                    Upon successful completion of this training, participants will be able to:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {meta.outcomes.map((outcome, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className={`h-6 w-6 ${standard.color.text} flex-shrink-0 mt-0.5`} />
                        <span className="text-gray-700">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.section>

              {/* Course Curriculum */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                  <BookOpen className={`h-8 w-8 ${standard.color.text}`} />
                  Detailed Curriculum
                </h2>
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                  <p className="text-gray-600 mb-6">
                    Our comprehensive {meta.duration} training covers the following key topics:
                  </p>
                  <div className="space-y-3">
                    {topics.map((topic, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${standard.color.bg} ${standard.color.text} font-bold flex-shrink-0`}>
                          {index + 1}
                        </div>
                        <span className="text-gray-700 pt-1">{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.section>

              {/* Who Should Attend */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                  <Users className={`h-8 w-8 ${standard.color.text}`} />
                  Who Should Attend
                </h2>
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                  <p className="text-gray-600 mb-6">
                    This {meta.title.toLowerCase()} is ideal for:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {meta.targetAudience.map((audience, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-gray-50">
                        <CheckCircle2 className={`h-6 w-6 ${standard.color.text} flex-shrink-0 mt-0.5`} />
                        <span className="text-gray-700">{audience}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.section>
            </div>

            {/* Sidebar - Right Side */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Actions Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Link
                      to="/training-calendar"
                      className={`w-full ${standard.color.bg} ${standard.color.text} px-6 py-3 rounded-xl font-semibold hover:shadow-md transition-all flex items-center justify-center gap-2`}
                    >
                      <Calendar className="h-5 w-5" />
                      View Schedule
                    </Link>
                    <button
                      className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                    >
                      <Download className="h-5 w-5" />
                      Download Brochure
                    </button>
                    <button
                      className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                    >
                      <Share2 className="h-5 w-5" />
                      Share Course
                    </button>
                  </div>
                </div>

                {/* Course Info Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <h3 className="text-xl font-bold mb-4">Course Information</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Prerequisites</p>
                      <p className="text-gray-700 font-medium">{meta.prerequisites}</p>
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-500 mb-1">Certification</p>
                      <p className="text-gray-700 font-medium">{meta.certification}</p>
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-500 mb-1">Training Format</p>
                      <p className="text-gray-700 font-medium">In-Person & Online</p>
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-500 mb-1">Language</p>
                      <p className="text-gray-700 font-medium">English</p>
                    </div>
                  </div>
                </div>

                {/* Other Training Levels */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <h3 className="text-xl font-bold mb-4">Other Training Levels</h3>
                  <div className="space-y-3">
                    {category !== 'awareness' && (
                      <Link
                        to={`/training/${standardSlug}/awareness`}
                        className="block p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                      >
                        <p className="font-semibold text-gray-900">Awareness Training</p>
                        <p className="text-sm text-gray-600">1 Day • Beginner</p>
                      </Link>
                    )}
                    {category !== 'internal-auditor' && (
                      <Link
                        to={`/training/${standardSlug}/internal-auditor`}
                        className="block p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                      >
                        <p className="font-semibold text-gray-900">Internal Auditor</p>
                        <p className="text-sm text-gray-600">3 Days • Intermediate</p>
                      </Link>
                    )}
                    {category !== 'lead-auditor' && (
                      <Link
                        to={`/training/${standardSlug}/lead-auditor`}
                        className="block p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                      >
                        <p className="font-semibold text-gray-900">Lead Auditor</p>
                        <p className="text-sm text-gray-600">5 Days • Advanced</p>
                      </Link>
                    )}
                    <Link
                      to={`/training/${standardSlug}`}
                      className="block p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-center"
                    >
                      <p className="font-semibold text-blue-600">View All Training Options</p>
                    </Link>
                  </div>
                </div>

                {/* Related Standards */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <h3 className="text-xl font-bold mb-4">Related Standards</h3>
                  <Link
                    to="/training/iso-training-services"
                    className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                  >
                    <Shield className="h-6 w-6 text-blue-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Browse All Standards</p>
                      <p className="text-sm text-gray-600">9 ISO/IATF Standards</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export { TrainingCategoryDetail };
export default TrainingCategoryDetail;
